import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Package, 
  Clock, 
  CheckCircle, 
  RefreshCw, 
  User, 
  Calendar,
  AlertCircle,
  Archive,
  Truck,
  Box
} from 'lucide-react';
import { API_URL } from '../../Config';

// Composant Toast
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-emerald-500' : 
                  type === 'error' ? 'bg-rose-500' : 
                  'bg-blue-500';

  return (
    <div className={`fixed top-6 right-6 z-50 px-4 py-3 ${bgColor} text-white rounded-lg shadow-lg transform transition-transform duration-300 animate-slide-in`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {type === 'success' && <CheckCircle className="w-5 h-5 mr-2" />}
          {type === 'error' && <AlertCircle className="w-5 h-5 mr-2" />}
          <span className="font-medium">{message}</span>
        </div>
        <button 
          onClick={onClose}
          className="ml-4 text-white hover:text-gray-200 focus:outline-none"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default function MagasinDashboard() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [stats, setStats] = useState({ aPreparer: 0, enPreparation: 0, pretes: 0 });
  const [demandesAPreparer, setDemandesAPreparer] = useState([]);
  const [demandesPretes, setDemandesPretes] = useState([]);

  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ ...toast, show: false });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [res1, res2] = await Promise.all([
        axios.get(`${API_URL}/magasin/validated-demandes`, { headers }),
        axios.get(`${API_URL}/recuperation/ready-demandes`, { headers })
      ]);

      const aPreparer = Array.isArray(res1.data.data) ? res1.data.data : res1.data.data?.data || [];
      const pretes = Array.isArray(res2.data.data) ? res2.data.data : res2.data.data?.data || [];

      setDemandesAPreparer(aPreparer);
      setDemandesPretes(pretes);

      setStats({
        aPreparer: aPreparer.length,
        enPreparation: aPreparer.filter(d => d.status === 'en_preparation').length,
        pretes: pretes.length
      });
    } catch (err) {
      showToast('Erreur lors du chargement des données', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusBadge = (status) => {
    const statusMap = {
      validee: { label: 'Validée', color: 'text-blue-800 bg-blue-100' },
      en_preparation: { label: 'En préparation', color: 'text-amber-800 bg-amber-100' },
      pret_recuperation: { label: 'Prête', color: 'text-emerald-800 bg-emerald-100' },
      partiellement_recuperee: { label: 'Partielle', color: 'text-indigo-800 bg-indigo-100' },
      completement_recupere: { label: 'Récupérée', color: 'text-gray-800 bg-gray-100' }
    };
    
    const s = statusMap[status] || { label: 'Inconnu', color: 'text-gray-800 bg-gray-100' };
    
    return (
      <span className={`text-xs px-3 py-1 rounded-full ${s.color}`}>
        {s.label}
      </span>
    );
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  }) : '—';

  const formatDemandeId = (id) => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `#DEM-${year}${month}-${String(id).padStart(4, '0')}`;
  };

  const hasRecuperation = (demande) => {
    return demande.demande_materiels?.some(item => (item.quantite_recuperee || 0) > 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Toast Notification */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={hideToast}
        />
      )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tableau de bord Magasin</h1>
              <p className="text-gray-500 mt-1">Gestion des demandes et récupérations</p>
            </div>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover: transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </button>
          </div>
          
          {/* Statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <div className="text-2xl font-semibold text-gray-900">{stats.aPreparer}</div>
                  <div className="text-sm text-gray-500">À préparer</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-semibold text-gray-900">{stats.enPreparation}</div>
                  <div className="text-sm text-gray-500">En préparation</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <div className="text-2xl font-semibold text-gray-900">{stats.pretes}</div>
                  <div className="text-sm text-gray-500">Prêtes</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Onglets */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setTabValue(0)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  tabValue === 0 
                    ? 'border-blue-600 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  À préparer
                  {stats.aPreparer > 0 && (
                    <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full">
                      {stats.aPreparer}
                    </span>
                  )}
                </div>
              </button>
              
              <button
                onClick={() => setTabValue(1)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  tabValue === 1 
                    ? 'border-emerald-600 text-emerald-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Prêtes
                  {stats.pretes > 0 && (
                    <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-800 rounded-full">
                      {stats.pretes}
                    </span>
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Contenu des onglets */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              {tabValue === 0 ? 'Demandes à préparer' : 'Demandes prêtes à récupérer'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {tabValue === 0 
                ? `${stats.aPreparer} demande${stats.aPreparer > 1 ? 's' : ''} en attente de préparation`
                : `${stats.pretes} demande${stats.pretes > 1 ? 's' : ''} disponible${stats.pretes > 1 ? 's' : ''}`
              }
            </p>
          </div>

          {tabValue === 0 ? (
            demandesAPreparer.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Archive className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Aucune demande à préparer</p>
                <p className="text-sm text-gray-400">
                  Les nouvelles demandes apparaîtront ici automatiquement
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {demandesAPreparer.map((demande) => (
                  <div key={demande.id} className="px-6 py-4 hover:">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                          <div className="font-medium text-gray-900">
                            {formatDemandeId(demande.id)}
                          </div>
                          {getStatusBadge(demande.status)}
                        </div>
                        
                        <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span>{demande.demandeur?.nom || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>{formatDate(demande.date_validation)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/magasin/preparer/${demande.id}`)}
                          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Préparer
                        </button>
                        {hasRecuperation(demande) && (
                          <button
                            onClick={() => navigate(`/magasin/suivi/${demande.id}`)}
                            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover: transition-colors"
                          >
                            Suivi
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            demandesPretes.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Aucune demande prête</p>
                <p className="text-sm text-gray-400">
                  Les demandes préparées apparaîtront ici
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {demandesPretes.map((demande) => (
                  <div key={demande.id} className="px-6 py-4 hover:">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                          <div className="font-medium text-gray-900">
                            {formatDemandeId(demande.id)}
                          </div>
                          {getStatusBadge(demande.status)}
                        </div>
                        
                        <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span>{demande.demandeur?.nom}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>{formatDate(demande.date_preparation)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Box className="w-4 h-4 text-gray-400" />
                            <span>{demande.demande_materiels?.length || 0} article{demande.demande_materiels?.length !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/magasin/recuperer/${demande.id}`)}
                          className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                          Récupérer
                        </button>
                        {hasRecuperation(demande) && (
                          <button
                            onClick={() => navigate(`/magasin/suivi/${demande.id}`)}
                            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover: transition-colors"
                          >
                            Suivi
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Légende */}
        <div className="mt-6 flex justify-center">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span>À préparer</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span>En préparation</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
              <span>Prêtes</span>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}